import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CardMedia from "@mui/material/CardMedia";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { getCategoriesList } from "../../../../../services/Admin/MenuApi";
import { getProductDetails, updateProduct } from "../../../../../services/Admin/MenuProductsApi";
import { resolveImageUrl } from "../../../../../services/indexMediaBaseUrl";

const initialForm = {
  name: "",
  description: "",
  price: "",
  category_id: "",
  prepare_time: "",
  status: 1,
  image: null,
  currentImage: null,
};

const EditeItemDialog = ({ open, onClose, productId, onUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    let mounted = true;

    const loadProduct = async () => {
      if (!open || !productId) return;

      setLoading(true);
      setError("");

      try {
        const res = await getProductDetails(productId);
        const product = res?.data || null;

        if (!mounted) return;

        setForm((prev) => ({
          ...prev,
          name: product?.name || product?.name_en || "",
          description: product?.description || product?.description_en || "",
          price: product?.price || "",
          category_id: product?.category_id || product?.category?.id || "",
          prepare_time: product?.prepare_time || "",
          status: product?.status ?? 1,
          currentImage: product?.image || null,
          image: null,
        }));
      } catch (err) {
        if (mounted) setError(err?.message || "Failed to load product details");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadProduct();
    return () => {
      mounted = false;
    };
  }, [open, productId]);

  useEffect(() => {
    let mounted = true;

    const loadCategories = async () => {
      setLoadingCategories(true);

      try {
        const res = await getCategoriesList();
        if (mounted) setCategories(res?.data || []);
      } catch (err) {
        // Keep the dialog usable even if categories fail to load.
      } finally {
        if (mounted) setLoadingCategories(false);
      }
    };

    if (open) loadCategories();
    return () => {
      mounted = false;
    };
  }, [open]);

  const handleChange = (key) => (event) => {
    let value;

    if (key === "image") {
      value = event.target.files?.[0] || null;
    } else if (key === "status") {
      value = Number(event.target.value);
    } else {
      value = event.target.value;
    }

    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccessMsg("");
    setLoading(true);

    try {
      const cleanName = String(form.name || "").trim();
      const cleanDescription = String(form.description || "").trim();

      const res = await updateProduct({
        id: productId,
        name: cleanName,
        name_en: cleanName,
        description: cleanDescription,
        description_en: cleanDescription,
        price: form.price,
        category_id: form.category_id,
        image: form.image,
        prepare_time: form.prepare_time,
        status: form.status,
      });

      setSuccessMsg("تم تحديث الصنف بنجاح");
      onUpdated?.(res?.data || null);
      onClose?.();
    } catch (err) {
      setError(err?.message || "فشل تحديث الصنف");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>تعديل الصنف</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ display: "grid", gap: 2, pt: 0.5 }}>
            {successMsg && <Alert severity="success">{successMsg}</Alert>}

            <TextField
              label="الاسم الجديد"
              value={form.name}
              onChange={handleChange("name")}
              fullWidth
              required
            />

            <TextField
              label="الوصف الجديد"
              value={form.description}
              onChange={handleChange("description")}
              fullWidth
              multiline
              rows={2}
            />

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <TextField
                label="السعر"
                value={form.price}
                onChange={handleChange("price")}
                fullWidth
              />

              <TextField
                label="زمن التحضير (دقيقة)"
                value={form.prepare_time}
                onChange={handleChange("prepare_time")}
                fullWidth
              />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="edit-product-category-label">الفئة</InputLabel>
                <Select
                  labelId="edit-product-category-label"
                  value={form.category_id}
                  label="الفئة"
                  onChange={handleChange("category_id")}
                  disabled={loadingCategories}
                >
                  <MenuItem value="">اختر الفئة</MenuItem>
                  {loadingCategories && <MenuItem disabled>جاري تحميل الفئات...</MenuItem>}
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="edit-product-status-label">الحالة</InputLabel>
                <Select
                  labelId="edit-product-status-label"
                  value={form.status}
                  label="الحالة"
                  onChange={handleChange("status")}
                >
                  <MenuItem value={1}>متاح</MenuItem>
                  <MenuItem value={0}>غير متاح</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "180px 1fr" }, gap: 2, alignItems: "center" }}>
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  الصورة الحالية
                </Typography>
                {form.currentImage ? (
                  <CardMedia
                    component="img"
                    src={resolveImageUrl(form.currentImage)}
                    sx={{ width: 160, height: 120, objectFit: "cover", borderRadius: 1 }}
                  />
                ) : (
                  <Typography variant="body2">لا توجد صورة</Typography>
                )}
              </Box>

              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  استبدال الصورة
                </Typography>
                <input type="file" accept="image/*" onChange={handleChange("image")} />
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          إلغاء
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : "حفظ"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditeItemDialog;
